import type {Filters} from '../types/filters';
import {type Project, type ProjectDraft, ProjectType} from '../types/project';

/**
 * Filters and sorts the projects by the given filters and sort direction
 */
export default function filterAndSortProjects(
  projects: Array<Project | ProjectDraft>,
  filters: Filters,
  sortDirection: 'asc' | 'desc',
  selectedProjectIds: Array<string>
): Array<Project | ProjectDraft> {
  const {
    searchText,
    typeFilter,
    confidentialityFilter,
    visibilityFilter,
    diamondFilter,
    customersFilter,
    teamFilter,
    fieldsFilter,
    topicsFilter,
    servicesFilter,
    dateRangeFilter,
    showDrafts,
    tasksFilter,
    technologiesFilter,
    formatsFilter,
    methodsFilter,
    valueInTimeFilter,
    valueInPriceFilter
  } = filters;

  const filteredProjects = projects.filter(project => {
    /**
     * General filters
     */
    const searchRegex = new RegExp(searchText || '', 'im');
    const matchesSearchText =
      searchRegex.test(project.type) ||
      searchRegex.test(project.title) ||
      searchRegex.test(project.title_En || '') ||
      searchRegex.test(project.description || '') ||
      searchRegex.test(project.description_En || '') ||
      searchRegex.test(project.customers?.join(', ') || '') ||
      searchRegex.test(project.team?.join(', ') || '') ||
      searchRegex.test(project.projectLead?.join(', ') || '') ||
      searchRegex.test(project.fields?.join(', ') || '') ||
      searchRegex.test(project.topics?.join(', ') || '') ||
      (project.type === ProjectType.DEVELOPMENT &&
        searchRegex.test(project.tasks?.join(', ') || '')) ||
      (project.type === ProjectType.DEVELOPMENT &&
        searchRegex.test(project.technologies?.join(', ') || '')) ||
      searchRegex.test(project.services?.join(', ') || '') ||
      (project.type === ProjectType.CONSULTING &&
        searchRegex.test(project.formats?.join(', ') || '')) ||
      (project.type === ProjectType.CONSULTING &&
        searchRegex.test(project.methods?.join(', ') || ''));
    const hasType = !typeFilter || project.type === typeFilter;

    const hasConfidentiality =
      !confidentialityFilter ||
      project.confidentiality === confidentialityFilter;

    const hasVisibility =
      !visibilityFilter || project.visibility === visibilityFilter;

    const hasDiamond =
      !diamondFilter.length ||
      diamondFilter.every(
        diamondSector => project.diamond && project.diamond[diamondSector] > 0
      );

    const hasCustomers =
      !customersFilter.length ||
      project.customers?.some(customer => customersFilter.includes(customer));

    const hasTeamOrProjectLead =
      !teamFilter.length ||
      project.team?.some(teamMember => teamFilter.includes(teamMember)) ||
      project.projectLead?.some(lead => teamFilter.includes(lead));

    const hasFields =
      !fieldsFilter.length ||
      project.fields?.some(field => fieldsFilter.includes(field));

    const hasTopics =
      !topicsFilter.length ||
      project.topics?.some(topic => topicsFilter.includes(topic));

    const hasServices =
      !servicesFilter.length ||
      project.services?.some(service => servicesFilter.includes(service));

    const createdAfterStartDate =
      !dateRangeFilter.end ||
      !project.creationPeriod?.start ||
      new Date(dateRangeFilter.end) >= project.creationPeriod.start;
    const createdBeforeEndDate =
      !dateRangeFilter.start ||
      !project.creationPeriod?.end ||
      new Date(dateRangeFilter.start) <= project.creationPeriod.end;

    const wasCreatedWithinDateRange =
      createdAfterStartDate && createdBeforeEndDate;

    const showProjectDraft = showDrafts || !project.isDraft;

    const meetsGeneralFilterCriteria =
      matchesSearchText &&
      hasType &&
      hasConfidentiality &&
      hasVisibility &&
      hasDiamond &&
      hasCustomers &&
      hasTeamOrProjectLead &&
      hasFields &&
      hasTopics &&
      hasServices &&
      wasCreatedWithinDateRange &&
      showProjectDraft;

    /**
     * Development filters
     */
    let meetsProjectFilterCriteria = false;

    const isAnyProjectFilterSet = Boolean(
      tasksFilter.length || technologiesFilter.length
    );

    if (project.type === ProjectType.DEVELOPMENT && isAnyProjectFilterSet) {
      const hasTasks = Boolean(
        !tasksFilter.length ||
          project.tasks?.some(task => tasksFilter.includes(task))
      );

      const hasTechnologies = Boolean(
        !technologiesFilter.length ||
          project.technologies?.some(technology =>
            technologiesFilter.includes(technology)
          )
      );

      meetsProjectFilterCriteria = hasTasks && hasTechnologies;
    }

    /**
     * Consulting filters
     */
    let meetsConsultingFilterCriteria = false;

    const isAnyConsultingFilterSet = Boolean(
      formatsFilter.length ||
        methodsFilter.length ||
        valueInTimeFilter.min != null ||
        valueInTimeFilter.max != null ||
        valueInPriceFilter.min != null ||
        valueInPriceFilter.max != null
    );

    if (project.type === ProjectType.CONSULTING && isAnyConsultingFilterSet) {
      const hasFormats =
        !formatsFilter.length ||
        Boolean(
          project.formats?.some(format => formatsFilter.includes(format))
        );

      const hasMethods =
        !methodsFilter.length ||
        Boolean(
          project.methods?.some(method => methodsFilter.includes(method))
        );

      const hasMinValueInTime =
        project.valueInTime == null ||
        valueInTimeFilter.min == null ||
        project.valueInTime >= valueInTimeFilter.min;
      const hasMaxValueInTime =
        project.valueInTime == null ||
        valueInTimeFilter.max == null ||
        project.valueInTime <= valueInTimeFilter.max;
      const hasValueInTime = hasMinValueInTime && hasMaxValueInTime;

      const hasMinValueInPrice =
        project.valueInPrice == null ||
        valueInPriceFilter.min == null ||
        project.valueInPrice >= valueInPriceFilter.min;
      const hasMaxValueInPrice =
        project.valueInPrice == null ||
        valueInPriceFilter.max == null ||
        project.valueInPrice <= valueInPriceFilter.max;
      const hasValueInPrice = hasMinValueInPrice && hasMaxValueInPrice;

      meetsConsultingFilterCriteria =
        hasFormats && hasMethods && hasValueInTime && hasValueInPrice;
    }

    return (
      meetsGeneralFilterCriteria &&
      ((!isAnyProjectFilterSet && !isAnyConsultingFilterSet) ||
        meetsProjectFilterCriteria ||
        meetsConsultingFilterCriteria)
    );
  });

  const filteredAndSortedProjects = filteredProjects.sort(
    (projectA, projectB) => {
      const isSelectedA = selectedProjectIds.includes(projectA.id);
      const isSelectedB = selectedProjectIds.includes(projectB.id);

      // Sort selected projects to the top
      if (isSelectedA !== isSelectedB) {
        return isSelectedB ? 1 : -1;
      }

      const startDateA = projectA.creationPeriod?.start
        ? projectA.creationPeriod.start.getTime()
        : 0;
      const startDateB = projectB.creationPeriod?.start
        ? projectB.creationPeriod.start.getTime()
        : 0;
      const endDateA = projectA.creationPeriod?.end
        ? projectA.creationPeriod.end.getTime()
        : startDateA;
      const endDateB = projectB.creationPeriod?.end
        ? projectB.creationPeriod.end.getTime()
        : startDateB;

      // First sort by end date, then by start date descending
      if (sortDirection === 'desc') {
        if (endDateB - endDateA !== 0) {
          return endDateB - endDateA;
        }
        return startDateB - startDateA;
      }

      // First sort by start date, then by end date ascending
      if (startDateA - startDateB !== 0) {
        return startDateA - startDateB;
      }
      return endDateA - endDateB;
    }
  );

  return filteredAndSortedProjects;
}
